import { AxiosInstance, AxiosRequestConfig } from 'axios';

import {
  VisitFormReport,
  VisitFormReportAttachment,
  VisitFormReportStatus,
  VisitFormTask,
  VisitFormTaskOutlet,
  VisitFormTaskRep,
  VisitFormTaskStatus,
  VisitFormTemplate,
  VisitFormTemplateStatus,
} from './models/visit-forms';

const ENDPOINT = 'visit_forms/';

export type ListPayload<Filter> = {
  limit: number;
  offset: number;
  sort: string;
  filter?: Filter;
};

// TEMPLATES
export type VisitFormTemplateCreatePayload = {
  task_id: number;
  structure: Record<string, any>;
  status?: VisitFormTemplateStatus;
};
export type VisitFormTemplateUpdatePayload = {
  structure?: Record<string, any>;
  status?: VisitFormTemplateStatus;
};

// TASKS
export type VisitFormTaskCreatePayload = {
  name: string;
  description?: string | null;
  date_start: string;
  date_end: string;
  priority?: string;
  status?: VisitFormTaskStatus;
  current_template_id?: number;
};
export type VisitFormTaskUpdatePayload = Partial<VisitFormTaskCreatePayload>;

// TASK REPS
export type VisitFormTaskRepCreatePayload = {
  task_id: number;
  login?: string | null;
  route_number_id?: string | null;
};
export type VisitFormTaskRepUpdatePayload = {
  login?: string | null;
  route_number_id?: string | null;
};

// TASK OUTLETS
export type VisitFormTaskOutletCreatePayload = {
  task_id: number;
  outlet_id?: number;
  segment_id?: number;
  channel?: string | null;
  subchannel_id?: number;
  subchannel?: string | null;
};
export type VisitFormTaskOutletUpdatePayload = Omit<VisitFormTaskOutletCreatePayload, 'task_id'>;

// REPORTS
export type VisitFormReportCreatePayload = {
  task_id: number;
  outlet_id: number;
  user_name: string;
  template_id: number;
  template_version: number;
  status?: VisitFormReportStatus;
  data?: Record<string, any>;
  validation_errors?: Record<string, any>;
  started_at?: string;
  submitted_at?: string;
  gps_lat?: number;
  gps_lng?: number;
};
export type VisitFormReportUpdatePayload = {
  data?: Record<string, any>;
  status?: VisitFormReportStatus;
  started_at?: string;
  submitted_at?: string;
  gps_lat?: number;
  gps_lng?: number;
  validation_errors?: Record<string, any>;
};

// REPORT ATTACHMENTS
export type VisitFormReportAttachmentCreatePayload = {
  report_id: number;
  field_id: string;
  block_instance: string;
  mime_type: string;
  size_bytes: number;
  checksum_sha256: string;
  s3_bucket: string;
  s3_key: string;
  original_name?: string | null;
  upload_status?: string;
  uploaded_at?: string;
  verified_at?: string;
};
export type VisitFormReportAttachmentUpdatePayload = {
  field_id?: string;
  block_instance?: string;
  mime_type?: string;
  size_bytes?: number;
  checksum_sha256?: string;
  original_name?: string | null;
  s3_bucket?: string;
  s3_key?: string;
  upload_status?: string;
  uploaded_at?: string;
  verified_at?: string;
};

// eslint-disable-next-line import/no-anonymous-default-export
export default (instance: AxiosInstance, config: AxiosRequestConfig) => ({
  // TEMPLATES
  getTemplates: (payload: ListPayload<Partial<Pick<VisitFormTemplate, 'id' | 'status' | 'task_id'>> & { created_by?: string }>) =>
    instance.post<ApiResponse<Paginated<'data', VisitFormTemplate>>>(ENDPOINT + 'templates/list/', payload, config),
  getTemplate: (templateId: number | string) =>
    instance.get<ApiResponse<VisitFormTemplate>>(ENDPOINT + `templates/info/${templateId}/`, config),
  createTemplate: (payload: VisitFormTemplateCreatePayload) =>
    instance.post<ApiResponse<{ id: number }>>(ENDPOINT + 'templates/create/', payload, config),
  updateTemplate: (templateId: number | string, payload: VisitFormTemplateUpdatePayload) =>
    instance.patch<ApiResponse<{ id: number }>>(ENDPOINT + `templates/update/${templateId}/`, payload, config),
  deleteTemplate: (templateId: number | string) =>
    instance.delete<ApiResponse<{ id: number }>>(ENDPOINT + `templates/delete/${templateId}/`, config),

  // TASKS
  getTasks: (payload: ListPayload<{ id?: number; name?: string; status?: VisitFormTaskStatus; priority?: string; current_template_id?: number; created_by?: string }>) =>
    instance.post<ApiResponse<Paginated<'data', VisitFormTask>>>(ENDPOINT + 'tasks/list/', payload, config),
  getTask: (taskId: number | string) =>
    instance.get<ApiResponse<VisitFormTask>>(ENDPOINT + `tasks/info/${taskId}/`, config),
  createTask: (payload: VisitFormTaskCreatePayload) =>
    instance.post<ApiResponse<{ id: number }>>(ENDPOINT + 'tasks/create/', payload, config),
  updateTask: (taskId: number | string, payload: VisitFormTaskUpdatePayload) =>
    instance.patch<ApiResponse<{ id: number }>>(ENDPOINT + `tasks/update/${taskId}/`, payload, config),
  deleteTask: (taskId: number | string) =>
    instance.delete<ApiResponse<{ id: number }>>(ENDPOINT + `tasks/delete/${taskId}/`, config),

  // TASK REPS
  getTaskReps: (payload: ListPayload<{ id?: number; task_id?: number; login?: string; route_number_id?: string }>) =>
    instance.post<ApiResponse<Paginated<'data', VisitFormTaskRep>>>(ENDPOINT + 'task_reps/list/', payload, config),
  getTaskRep: (pid: number | string) =>
    instance.get<ApiResponse<VisitFormTaskRep>>(ENDPOINT + `task_reps/info/${pid}/`, config),
  createTaskRep: (payload: VisitFormTaskRepCreatePayload) =>
    instance.post<ApiResponse<{ id: number }>>(ENDPOINT + 'task_reps/create/', payload, config),
  updateTaskRep: (pid: number | string, payload: VisitFormTaskRepUpdatePayload) =>
    instance.patch<ApiResponse<{ id: number }>>(ENDPOINT + `task_reps/update/${pid}/`, payload, config),
  deleteTaskRep: (pid: number | string) =>
    instance.delete<ApiResponse<{ id: number }>>(ENDPOINT + `task_reps/delete/${pid}/`, config),

  // TASK OUTLETS
  getTaskOutlets: (payload: ListPayload<{ id?: number; task_id?: number; outlet_id?: number; segment_id?: number; channel?: string; subchannel_id?: number; subchannel?: string }>) =>
    instance.post<ApiResponse<Paginated<'data', VisitFormTaskOutlet>>>(ENDPOINT + 'task_outlets/list/', payload, config),
  getTaskOutlet: (pid: number | string) =>
    instance.get<ApiResponse<VisitFormTaskOutlet>>(ENDPOINT + `task_outlets/info/${pid}/`, config),
  createTaskOutlet: (payload: VisitFormTaskOutletCreatePayload) =>
    instance.post<ApiResponse<{ id: number }>>(ENDPOINT + 'task_outlets/create/', payload, config),
  updateTaskOutlet: (pid: number | string, payload: VisitFormTaskOutletUpdatePayload) =>
    instance.patch<ApiResponse<{ id: number }>>(ENDPOINT + `task_outlets/update/${pid}/`, payload, config),
  deleteTaskOutlet: (pid: number | string) =>
    instance.delete<ApiResponse<{ id: number }>>(ENDPOINT + `task_outlets/delete/${pid}/`, config),

  // REPORTS
  getReports: (payload: ListPayload<{ id?: number; task_id?: number; outlet_id?: number; user_name?: string; template_id?: number; status?: VisitFormReportStatus }>) =>
    instance.post<ApiResponse<Paginated<'data', VisitFormReport>>>(ENDPOINT + 'reports/list/', payload, config),
  getReport: (reportId: number | string) =>
    instance.get<ApiResponse<VisitFormReport>>(ENDPOINT + `reports/info/${reportId}/`, config),
  createReport: (payload: VisitFormReportCreatePayload) =>
    instance.post<ApiResponse<{ id: number }>>(ENDPOINT + 'reports/create/', payload, config),
  updateReport: (reportId: number | string, payload: VisitFormReportUpdatePayload) =>
    instance.patch<ApiResponse<{ id: number }>>(ENDPOINT + `reports/update/${reportId}/`, payload, config),
  deleteReport: (reportId: number | string) =>
    instance.delete<ApiResponse<{ id: number }>>(ENDPOINT + `reports/delete/${reportId}/`, config),

  // REPORT ATTACHMENTS
  getReportAttachments: (payload: ListPayload<{ id?: number; report_id?: number; field_id?: string; block_instance?: string; mime_type?: string; upload_status?: string; s3_bucket?: string; original_name?: string }>) =>
    instance.post<ApiResponse<Paginated<'data', VisitFormReportAttachment>>>(ENDPOINT + 'report_attachments/list/', payload, config),
  getReportAttachment: (pid: number | string) =>
    instance.get<ApiResponse<VisitFormReportAttachment>>(ENDPOINT + `report_attachments/info/${pid}/`, config),
  createReportAttachment: (payload: VisitFormReportAttachmentCreatePayload) =>
    instance.post<ApiResponse<{ id: number }>>(ENDPOINT + 'report_attachments/create/', payload, config),
  updateReportAttachment: (pid: number | string, payload: VisitFormReportAttachmentUpdatePayload) =>
    instance.patch<ApiResponse<{ id: number }>>(ENDPOINT + `report_attachments/update/${pid}/`, payload, config),
  deleteReportAttachment: (pid: number | string) =>
    instance.delete<ApiResponse<{ id: number }>>(ENDPOINT + `report_attachments/delete/${pid}/`, config),
});
